import { useState } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import {
  apiServicesService,
  type ApiInstanceAdoption,
  type ApiService,
  type CreateServerResponse,
} from '@/deploy/services/api-services.service';
import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import { ENVIRONMENT_BY_ID } from '@/deploy/types';
import { apiGroupLabel, canonicalApiEnv, databaseNameMatches, isDatabaseApi } from '@/deploy/utils/apiEnvs';
import {
  DB_SUFFIX_PATTERN, DEFAULT_INSTANCE, apiInstances, deriveInstanceKey, findInstance, hasServer,
  instanceDisplayName, instanceErrorMessage, instanceLabel, instanceLabelError, isDefaultInstance, versionOn,
} from '@/deploy/utils/apiInstances';
import pageStyles from '@/deploy/pages/ApiServices.module.scss';
import styles from './ApiInstances.module.scss';
import { DatabaseConfirmField, databaseConfirmErrorMessage } from './DatabaseConfirm';

// ============================================================
// Instance d'une API : création en deux étapes (instance, puis serveur sur l'env courant),
// renommage, ou création du serveur seule (« Créer le serveur… »).
// ============================================================

interface ApiInstanceModalProps {
  projectId: string;
  /** API à jour : la page la relit après chaque étape (le rattachement change la liste live). */
  api: ApiService;
  /** Env de l'onglet : c'est là que l'étape 2 crée le serveur. */
  envId: ApiEnvId;
  mode: 'create' | 'rename' | 'server';
  /** Clé de l'instance (rename, server). */
  instanceKey?: string;
  /** Création depuis la ligne d'un groupe : son libellé (« Iris »). */
  initialLabel?: string;
  onClose: () => void;
  /** Recharge la liste des APIs. */
  onChanged: () => Promise<void> | void;
  /** Création réussie : l'appelant présélectionne l'instance (la répartition reste à enregistrer). */
  onCreated?: (key: string) => void;
}

export function ApiInstanceModal(props: ApiInstanceModalProps) {
  const { mode } = props;
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [adoption, setAdoption] = useState<ApiInstanceAdoption[] | undefined>(undefined);

  if (mode === 'rename') return <RenameStep {...props} />;
  if (mode === 'server' && props.instanceKey) {
    return <ServerStep {...props} instanceKey={props.instanceKey} />;
  }
  if (createdKey) return <ServerStep {...props} instanceKey={createdKey} adoption={adoption} />;
  return (
    <CreateStep {...props} onDone={(key, a) => { setAdoption(a); setCreatedKey(key); }} />
  );
}

// ---- Étape 1 : nouvelle instance ----
function CreateStep({ projectId, api, initialLabel, onClose, onChanged, onCreated, onDone }: ApiInstanceModalProps & {
  onDone: (key: string, adoption?: ApiInstanceAdoption[]) => void;
}) {
  const [label, setLabel] = useState(initialLabel ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keys = apiInstances(api).map((i) => i.key);
  const key = deriveInstanceKey(label, keys);
  const labelError = label.trim() ? instanceLabelError(api, label) : null;

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiServicesService.createInstance(projectId, api.id, { label: label.trim() });
      await onChanged();
      onCreated?.(res.key);
      onDone(res.key, res.adoption);
    } catch (e) {
      setError(instanceErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Nouvelle instance de ${api.name}`}
      subtitle="Un exemplaire de l'API avec son propre serveur Ptero par environnement"
      width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="primary" onClick={() => void submit()} loading={submitting}
          disabled={submitting || !label.trim() || !!labelError}>
          Créer l'instance
        </Button>
      </>}
    >
      <div className={pageStyles.form}>
        {error && <div className={pageStyles.error}>{error}</div>}
        <label className={pageStyles.field}>
          <span>Libellé <em>requis</em></span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Iris" maxLength={32} autoFocus />
          {labelError && <div className={[pageStyles.fieldHint, pageStyles.fieldHintDanger].join(' ')}>{labelError}</div>}
        </label>
        <div className={pageStyles.fieldHint}>
          Identifiant : <code>{key}</code>. Déduit du libellé. Stable : sert dans les URL, le verrou,
          l'override et la répartition. Non modifiable ensuite.
        </div>
        <div className={pageStyles.fieldHint}>
          Serveur Ptero : <code>{instanceDisplayName(api, { key, label: label.trim() || key })}</code>
        </div>
      </div>
    </Modal>
  );
}

// ---- Renommage ----
function RenameStep({ projectId, api, instanceKey, onClose, onChanged }: ApiInstanceModalProps) {
  const inst = instanceKey ? findInstance(api, instanceKey) : undefined;
  const [label, setLabel] = useState(inst ? instanceLabel(inst) : '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!inst) return null;
  const labelError = label.trim() ? instanceLabelError(api, label, inst.key) : null;
  const unchanged = label.trim() === instanceLabel(inst);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await apiServicesService.renameInstance(projectId, api.id, inst.key, label.trim());
      await onChanged();
      onClose();
    } catch (e) {
      setError(instanceErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Instance ${instanceLabel(inst)} de ${api.name}`}
      subtitle={`Identifiant ${inst.key} (non modifiable)`}
      width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="primary" onClick={() => void submit()} loading={submitting}
          disabled={submitting || !label.trim() || !!labelError || unchanged}>
          Renommer
        </Button>
      </>}
    >
      <div className={pageStyles.form}>
        {error && <div className={pageStyles.error}>{error}</div>}
        <label className={pageStyles.field}>
          <span>Libellé <em>requis</em></span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={32} autoFocus />
          {labelError && <div className={[pageStyles.fieldHint, pageStyles.fieldHintDanger].join(' ')}>{labelError}</div>}
        </label>
        <div className={pageStyles.fieldHint}>
          Le nom des serveurs Ptero déjà créés ne change pas : c'est cosmétique.
        </div>
      </div>
    </Modal>
  );
}

/** Statut d'adoption de la Principale sans message du CICD : texte affiché. */
const ADOPTION_TEXT: Partial<Record<ApiInstanceAdoption['status'], string>> = {
  ambiguous: 'plusieurs serveurs correspondent, à rattacher à la main',
  missing: 'le serveur rattaché à la Principale est introuvable sur Ptero',
  broken: "le serveur rattaché à la Principale n'a pas l'image de l'API (lien cassé)",
  unreadable: 'Pterodactyl illisible, rien rattaché',
};

/** Texte de l'encadré d'adoption : serveurs rattachés à la Principale à la première instance. */
function adoptionSummary(api: ApiService, adoption: ApiInstanceAdoption[]): { bound: string[]; others: string[] } {
  const bound: string[] = [];
  const others: string[] = [];
  for (const a of adoption) {
    const env = apiGroupLabel(a.environment);
    if (a.status === 'bound') bound.push(`${env} (${a.serverName ?? api.name})`);
    else if (a.status !== 'none') others.push(`${env} : ${a.message ?? ADOPTION_TEXT[a.status] ?? a.status}`);
  }
  return { bound, others };
}

// ---- Étape 2 : serveur de l'instance sur l'env courant ----
function ServerStep({ projectId, api, envId, instanceKey, adoption, onClose, onChanged }: ApiInstanceModalProps & {
  instanceKey: string;
  adoption?: ApiInstanceAdoption[];
}) {
  const env = canonicalApiEnv(envId);
  const envLabel = apiGroupLabel(env);
  const all = apiInstances(api);
  const principal = all[0];
  const inst = findInstance(api, instanceKey) ?? { key: instanceKey, label: instanceKey, isDefault: false };
  const name = instanceDisplayName(api, inst);
  // Sources possibles : les autres instances qui ont un serveur utilisable sur l'env. Aucune pour
  // la Principale : copiée depuis une autre instance, elle pointerait sur sa base (refusé par le CICD).
  const principalTarget = isDefaultInstance(inst);
  const sources = principalTarget ? [] : all.filter((i) => i.key !== inst.key && hasServer(i, env));
  const defaultSource = !principalTarget && sources.some(isDefaultInstance) ? DEFAULT_INSTANCE : '';

  const [version, setVersion] = useState(versionOn(principal, env) ?? api.latestAvailable ?? '');
  const [ramMb, setRamMb] = useState(1024);
  const [diskMb, setDiskMb] = useState(5000);
  const [cpuPct, setCpuPct] = useState(200);
  const [source, setSource] = useState(defaultSource);
  const [suffix, setSuffix] = useState(principalTarget ? '' : `_${inst.key}`);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateServerResponse | null>(null);
  // Base de données : la création attend son nom retapé, envoyé au CICD
  const database = isDatabaseApi(api);
  const [dbTyped, setDbTyped] = useState('');
  const dbOk = !database || databaseNameMatches(api, dbTyped);

  const suffixError = source && suffix.trim() && !DB_SUFFIX_PATTERN.test(suffix.trim())
    ? 'Lettres, chiffres, _ et -, 32 caractères au plus' : null;
  const adopted = adoption && adoption.length > 0 ? adoptionSummary(api, adoption) : null;

  const submit = async () => {
    if (!dbOk) return;
    // Base de données : le nom retapé tient lieu de cette confirmation
    if (!database && ENVIRONMENT_BY_ID[env].requiresConfirmation
      && !confirm(`Créer le serveur de ${name} sur ${envLabel} en v${version || '?'}${source ? `, avec la configuration de ${instanceLabel(findInstance(api, source) ?? { key: source, label: source })}` : ''} ?`)) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiServicesService.createServer(projectId, api.id, {
        environment: env,
        version: version || undefined,
        memoryMb: ramMb, diskMb, cpuPct,
        instance: isDefaultInstance(inst) ? undefined : inst.key,
        copyConfigFrom: source || undefined,
        databaseSuffix: source && suffix.trim() ? suffix.trim() : undefined,
        confirmDatabase: database ? api.name : undefined,
      });
      setResult(res);
      await onChanged();
    } catch (e) {
      setError(databaseConfirmErrorMessage(e, api) ?? instanceErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = result?.copyConfig;
  return (
    <Modal
      open onClose={onClose}
      title={`Créer le serveur de ${name} sur ${envLabel} ?`}
      subtitle={result ? 'Serveur créé' : "L'instance n'est servie qu'aux groupes que la répartition lui envoie"}
      width="md"
      footer={result ? (
        <Button variant="primary" onClick={onClose}>Fermer</Button>
      ) : (<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Plus tard</Button>
        <Button variant="primary" onClick={() => void submit()} loading={submitting}
          disabled={submitting || !!suffixError || !dbOk} leftIcon={<Icon name="rocket" size={12} />}>
          Créer le serveur
        </Button>
      </>)}
    >
      <div className={pageStyles.form}>
        {adopted && (
          <div className={styles.helpBox}>
            L'instance existante devient « Principale ».
            {adopted.bound.length > 0 && <> Serveurs rattachés : {adopted.bound.join(', ')}.</>}
            {adopted.others.map((o) => <div key={o}>{o}.</div>)}
          </div>
        )}
        {error && <div className={pageStyles.error}>{error}</div>}

        {!result && (<>
          <label className={pageStyles.field}>
            <span>Version <em>(vide = latestAvailable)</em></span>
            <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder={api.latestAvailable ?? '0.1.0'} />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            <label className={pageStyles.field}>
              <span>RAM (Mo)</span>
              <input type="number" value={ramMb} onChange={(e) => setRamMb(Number(e.target.value) || 0)} />
            </label>
            <label className={pageStyles.field}>
              <span>Disk (Mo)</span>
              <input type="number" value={diskMb} onChange={(e) => setDiskMb(Number(e.target.value) || 0)} />
            </label>
            <label className={pageStyles.field}>
              <span>CPU (%) <em>100 = 1 core</em></span>
              <input type="number" value={cpuPct} onChange={(e) => setCpuPct(Number(e.target.value) || 0)} />
            </label>
          </div>
          {principalTarget ? (
            <div className={pageStyles.fieldHint}>
              Configuration initiale : celle de l'image. La Principale ne copie pas la config d'une autre instance
              (elle pointerait sur sa base) : configure-la ensuite avec Fichiers.
            </div>
          ) : (
            <CopyConfigFields api={api} envId={env} sources={sources} source={source} onSource={setSource}
              suffix={suffix} onSuffix={setSuffix} suffixError={suffixError} />
          )}
          {database && (
            <DatabaseConfirmField
              api={api}
              summary={<>
                Le serveur de {name} sur {envLabel} démarrera sur l'image v{version || '?'} de <code>{api.name}</code>.
              </>}
              value={dbTyped}
              onChange={setDbTyped}
              disabled={submitting}
            />
          )}
          {submitting && (
            <div className={[pageStyles.resultRow, pageStyles.resultSkip].join(' ')}>
              <Icon name="refresh" size={12} />
              <span style={{ flex: 1 }}>
                Création du serveur{source ? ", puis attente de l'installation avant la copie de config (1 min 30 au plus)" : ''}…
              </span>
            </div>
          )}
        </>)}

        {result && (
          <div className={pageStyles.results}>
            <div className={[pageStyles.resultRow, pageStyles.resultOk].join(' ')}>
              <Icon name="check" size={12} />
              <span style={{ flex: 1 }}>
                {result.serverName ?? name} créé{result.newImage && <> : <code>{result.newImage}</code></>}
              </span>
            </div>
            {copy && copy.ok && (
              <div className={[pageStyles.resultRow, pageStyles.resultOk].join(' ')}>
                <Icon name="check" size={12} />
                <span style={{ flex: 1 }}>
                  {copy.message ?? 'Configuration copiée'}
                  {copy.changed.length > 0 && <> · champs suffixés : {copy.changed.join(', ')}</>}
                </span>
              </div>
            )}
            {copy && !copy.ok && (
              <div className={[pageStyles.resultRow, pageStyles.resultWarn].join(' ')}>
                <Icon name="warn" size={12} />
                <span style={{ flex: 1 }}>Configuration manuelle requise : {copy.error ?? 'copie impossible'}</span>
              </div>
            )}
            {copy?.warning && (
              <div className={[pageStyles.resultRow, pageStyles.resultWarn].join(' ')}>
                <Icon name="warn" size={12} />
                <span style={{ flex: 1 }}>{copy.warning}</span>
              </div>
            )}
            {result.warning && (
              <div className={[pageStyles.resultRow, pageStyles.resultWarn].join(' ')}>
                <Icon name="warn" size={12} />
                <span style={{ flex: 1 }}>{result.warning}</span>
              </div>
            )}
            {!isDefaultInstance(inst) && (
              <div className={pageStyles.fieldHint}>
                L'instance n'est pas encore répartie : choisis-la pour un groupe dans « Répartition » puis enregistre.
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Champs « Configuration initiale » et « Suffixe du nom de base », communs à la création du
 * serveur d'une instance, au bloc « Créer » du déploiement et à la copie de config.
 */
export function CopyConfigFields({ api, envId, sources, source, onSource, suffix, onSuffix, suffixError, allowBlank = true }: {
  api: ApiService;
  envId: ApiEnvId;
  sources: { key: string; label: string }[];
  source: string;
  onSource: (key: string) => void;
  suffix: string;
  onSuffix: (s: string) => void;
  suffixError: string | null;
  /** Option « Vierge » (config de l'image) ; absente pour une copie sur un serveur existant. */
  allowBlank?: boolean;
}) {
  const envLabel = apiGroupLabel(envId);
  return (<>
    <label className={pageStyles.field}>
      <span>Configuration initiale</span>
      <select value={source} onChange={(e) => onSource(e.target.value)}>
        {sources.map((s) => (
          <option key={s.key} value={s.key}>Copier depuis {instanceLabel(s)} ({envLabel})</option>
        ))}
        {allowBlank && <option value="">Vierge (appsettings de l'image)</option>}
      </select>
      {allowBlank && sources.length === 0 && (
        <div className={pageStyles.fieldHint}>Aucune autre instance de {api.name} n'a de serveur sur {envLabel} : rien à copier.</div>
      )}
    </label>
    {source && (
      <label className={pageStyles.field}>
        <span>Suffixe du nom de base</span>
        <input value={suffix} onChange={(e) => onSuffix(e.target.value)} placeholder="_iris" maxLength={32} />
        {suffixError && <div className={[pageStyles.fieldHint, pageStyles.fieldHintDanger].join(' ')}>{suffixError}</div>}
        <div className={pageStyles.fieldHint}>
          Ajouté à Database.DatabaseName ou DatabaseSettings.DatabaseName. Vide = même base que l'instance copiée.
          Les chaînes de connexion (Mongo, RabbitMQ, Redis) sont copiées telles quelles : à corriger avec Fichiers
          si ce groupe a sa propre infra.
        </div>
      </label>
    )}
  </>);
}
